import { createContext, useContext, useState, ReactNode } from 'react';

export interface SelectedProduct {
  id: string;
  name: string;
  setupPrice: string;
  recurringPrice: string;
  billingCycle: string;
}

export interface SelectedAddOn {
  id: string;
  name: string;
  price: string;
  category: string;
}

interface ProductSelectionContextType {
  selectedProduct: SelectedProduct | null;
  selectedAddOns: SelectedAddOn[];
  availableSlots: number;
  selectProduct: (product: SelectedProduct) => void;
  toggleAddOn: (addOn: SelectedAddOn) => void;
  clearSelection: () => void;
  getTotal: () => string;
}

const ProductSelectionContext = createContext<ProductSelectionContextType | undefined>(undefined);

export function ProductSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null);
  const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOn[]>([]);
  
  // FOMO: Limited monthly build slots
  const availableSlots = 2; // Limited build capacity - creates natural urgency

  const selectProduct = (product: SelectedProduct) => {
    setSelectedProduct(product);
  };

  const toggleAddOn = (addOn: SelectedAddOn) => {
    setSelectedAddOns(prev => {
      const exists = prev.find(item => item.id === addOn.id);
      if (exists) {
        return prev.filter(item => item.id !== addOn.id);
      } else {
        return [...prev, addOn];
      }
    });
  };

  const clearSelection = () => {
    setSelectedProduct(null);
    setSelectedAddOns([]);
  };

  const getTotal = () => {
    if (!selectedProduct) return "$0";
    
    const setupPrice = parseInt(selectedProduct.setupPrice.replace(/[,$]/g, ''));
    const addOnTotal = selectedAddOns.reduce((total, addOn) => {
      return total + parseInt(addOn.price.replace(/[,$]/g, ''));
    }, 0);
    
    return `$${(setupPrice + addOnTotal).toLocaleString()}`;
  };

  return (
    <ProductSelectionContext.Provider value={{
      selectedProduct,
      selectedAddOns,
      availableSlots,
      selectProduct,
      toggleAddOn,
      clearSelection,
      getTotal
    }}>
      {children}
    </ProductSelectionContext.Provider>
  );
}

export function useProductSelection() {
  const context = useContext(ProductSelectionContext);
  if (context === undefined) {
    throw new Error('useProductSelection must be used within a ProductSelectionProvider');
  }
  return context;
}
